import { BandwidthUA } from '@bandwidth/bw-webrtc-sdk';
import { CallToken, CallTypes, Concierge, Lead } from 'typings';
import store from 'state/store';
import { setCallType, setEndCall, setCallStream, setIncomingCall } from 'state/call.state';
import { Buffer } from 'buffer';
import { setNotification } from 'state/app.state';

let activeCall: any;
let phone: any;

export class BandwidthClient {
  async initCallDevice(callToken: CallToken) {
    if (!phone?.isInitialized()) {
      phone = new BandwidthUA();
      phone.setServerConfig(
        ['wss://gw.webrtc-app.bandwidth.com:10081'],
        'gw.webrtc-app.bandwidth.com',
        ['stun.l.google.com:19302', 'stun1.l.google.com:19302', 'stun2.l.google.com:19302']
      );
      phone.setOAuthToken(callToken.token);

      this.registerBandwidthEvents();

      // Make sure there's audio input and output
      await phone.checkAvailableDevices();

      phone.setAccount(process.env.REACT_APP_BANDWIDTH_CONCIERGE_NUMBER, '', '');
      await phone.init();
      console.log('Initialized Bandwidth device');
    } else {
      phone.setOAuthToken(callToken.token);
      console.log('Updated Bandwidth device token');
    }
  }

  private registerBandwidthEvents() {
    phone.setListeners({
      loginStateChanged: function (isLogin: any, cause: any) {
        console.info('Bandwidth event: loginStateChanged', { isLogin, cause });
      },
      outgoingCallProgress: function (call: any, response: any) {
        console.info('Bandwidth event: outgoingCallProgress', { call, response });
      },
      callTerminated: function (call: any, message: any, cause: any) {
        console.info('Bandwidth event: callTerminated', { call, message, cause });
        activeCall = null;
        store.dispatch(setEndCall());

        if (cause === 'Authentication Error') {
          store.dispatch(
            setNotification({ text: 'Call token expired, please refresh', type: 'error' })
          );
        }
      },
      callConfirmed: function (call: any, message: any, cause: any) {
        console.info('Bandwidth event: callConfirmed', { call, message, cause });

        store.dispatch(setIncomingCall(false));
      },
      incomingCall: function (call: any, invite: any) {
        console.info('Bandwidth event: incomingCall', { call, invite });
      },
      callHoldStateChanged: function (call: any, isHold: any, isRemote: any) {
        console.info('Bandwidth event: callHoldStateChanged', { call, isHold, isRemote });
      },
      callShowStreams: function (call: any, localStream: any, remoteStream: MediaStream) {
        console.info('Bandwidth event: callShowStreams', { call, localStream, remoteStream });
        activeCall = call;
        store.dispatch(setCallStream(remoteStream));
      },
    });
  }

  dialOutbound(lead: Lead, concierge: Concierge) {
    const uuiToken = Buffer.from(`direction=outbound&leadId=${lead.id}&conciergeId=${concierge.id}`)
      .toString('base64')
      .replaceAll('=', '');

    phone.call(process.env.REACT_APP_BANDWIDTH_CONCIERGE_NUMBER, [
      `User-to-User:${uuiToken};encoding=base64`,
    ]);

    store.dispatch(setCallType(CallTypes.OUTBOUND));
  }

  acceptInbound(lead: Lead, concierge: Concierge) {
    const uuiToken = Buffer.from(`direction=inbound&leadId=${lead.id}&conciergeId=${concierge.id}`)
      .toString('base64')
      .replaceAll('=', '');

    phone.call(process.env.REACT_APP_BANDWIDTH_CONCIERGE_NUMBER, [
      `User-to-User:${uuiToken};encoding=base64`,
    ]);

    store.dispatch(setCallType(CallTypes.INBOUND));
  }

  muteCall(shouldMute: boolean) {
    if (!activeCall || activeCall.isAudioMuted() === shouldMute) return;

    activeCall.muteAudio(shouldMute);
  }

  endCall() {
    if (!activeCall) return;

    activeCall.terminate();
  }

  sendDigits(digits: string) {
    if (!activeCall) return;

    activeCall.sendDTMF(digits);
  }

  needsRing() {
    return true;
  }
}
